import type { ApiErrorCode, MyVotesResponse } from "@vote/shared";
import { getSharedIdentification } from "./identity";
import type { ApiFailure, ApiResult, IdentityStore, VoteApi, Wait } from "./ports";
import { withServiceBusyRetry } from "./service-busy-retry";

export type MyVote = MyVotesResponse["votes"][number];

/** 個人画面の表示状態を、初回読込・一覧・空・失敗の排他的な状態として表す。 */
export type MyVotesFlowState =
  | { kind: "loading" }
  | {
      kind: "list";
      votes: MyVote[];
      nextCursor: string | null;
      isLoadingMore: boolean;
    }
  | { kind: "empty" }
  | { kind: "error"; code: ApiErrorCode | "unknown"; message: string };

/** 状態変化のたびに個人画面UIへ最新状態を渡す購読者。 */
type MyVotesFlowSubscriber = (state: MyVotesFlowState) => void;

/** 回答履歴フローが外部へ依頼する機能と埋め込み先のサイトキー。 */
interface MyVotesFlowDependencies {
  readonly siteKey: string;
  readonly api: VoteApi;
  readonly identityStore: IdentityStore;
  readonly wait: Wait;
}

/** API失敗を、UIが回復可能性を判定できる構造化状態へ変換する。 */
function errorState(failure: ApiFailure): Extract<MyVotesFlowState, { kind: "error" }> {
  return { kind: "error", code: failure.error.code, message: failure.error.message };
}

/** 予期しない例外をunknownとして保持し、既知のAPIエラーと混同しない。 */
function unknownErrorState(error: unknown): Extract<MyVotesFlowState, { kind: "error" }> {
  return {
    kind: "error",
    code: "unknown",
    message: error instanceof Error ? error.message : "unknown_error",
  };
}

/** identify後の回答履歴取得、追い読み、自己修復をDOMなしで管理する。 */
export class MyVotesFlow {
  private state: MyVotesFlowState = { kind: "loading" };
  private readonly subscribers = new Set<MyVotesFlowSubscriber>();

  public constructor(private readonly dependencies: MyVotesFlowDependencies) {}

  /** 現在の表示状態を、主にUIの初期描画とテストへ返す。 */
  public getState(): MyVotesFlowState {
    return this.state;
  }

  /** 購読開始時にも現在値を通知し、解除関数を返す。 */
  public subscribe(subscriber: MyVotesFlowSubscriber): () => void {
    this.subscribers.add(subscriber);
    subscriber(this.state);
    return () => this.subscribers.delete(subscriber);
  }

  /** identifyを先に完了し、既定件数の回答履歴を一覧または空状態として読み込む。 */
  public async start(): Promise<void> {
    this.publish({ kind: "loading" });

    try {
      const identityResult = await getSharedIdentification({
        siteKey: this.dependencies.siteKey,
        api: this.dependencies.api,
        identityStore: this.dependencies.identityStore,
        wait: this.dependencies.wait,
      });
      if (!identityResult.ok) {
        this.publish(errorState(identityResult));
        return;
      }

      // identifyの採用IDを保存してからme/votesへ送り、Cookieが使えない環境でも本人を特定する。
      this.dependencies.identityStore.set(identityResult.data.visitorId);
      const historyResult = await this.getMyVotesWithBusyRetry();
      if (!historyResult.ok) {
        this.publish(errorState(historyResult));
        return;
      }

      // Cookie優先で変わり得る採用IDが返った場合だけ、localStorageを同じIDへ自己修復する。
      if (historyResult.data.visitorId !== null) {
        this.dependencies.identityStore.set(historyResult.data.visitorId);
      }
      if (historyResult.data.votes.length === 0) {
        this.publish({ kind: "empty" });
        return;
      }
      this.publish({
        kind: "list",
        votes: historyResult.data.votes,
        nextCursor: historyResult.data.nextCursor,
        isLoadingMore: false,
      });
    } catch (error) {
      this.publish(unknownErrorState(error));
    }
  }

  /** 次のcursorページを末尾へ連結し、失敗時は表示済み履歴を維持する。 */
  public async loadMore(): Promise<void> {
    if (this.state.kind !== "list" || this.state.nextCursor === null || this.state.isLoadingMore) {
      return;
    }

    // 再描画後の状態ではcursorが変わるため、開始時点の値をこの追い読み専用に固定する。
    const cursor = this.state.nextCursor;
    const previousState = this.state;
    this.publish({ ...previousState, isLoadingMore: true });
    try {
      const result = await this.getMyVotesWithBusyRetry(cursor);
      if (!result.ok) {
        console.warn(`[vote-sdk] 回答履歴の追加読込に失敗しました: ${result.error.code}`);
        this.publish({ ...previousState, isLoadingMore: false });
        return;
      }

      if (result.data.visitorId !== null) {
        this.dependencies.identityStore.set(result.data.visitorId);
      }
      this.publish({
        kind: "list",
        votes: [...previousState.votes, ...result.data.votes],
        nextCursor: result.data.nextCursor,
        isLoadingMore: false,
      });
    } catch (error) {
      console.warn("[vote-sdk] 回答履歴の追加読込中に予期しないエラーが発生しました", error);
      this.publish({ ...previousState, isLoadingMore: false });
    }
  }

  /** 初回エラー状態からidentifyと履歴取得をやり直す。 */
  public async retry(): Promise<void> {
    if (this.state.kind === "error") {
      await this.start();
    }
  }

  /** 現在の採用IDと任意のcursorを添え、履歴取得の一時的混雑だけを一度再送する。 */
  private getMyVotesWithBusyRetry(cursor?: string): Promise<ApiResult<MyVotesResponse>> {
    const visitorId = this.dependencies.identityStore.get();
    const request = {
      siteKey: this.dependencies.siteKey,
      ...(visitorId === undefined ? {} : { visitorId }),
      ...(cursor === undefined ? {} : { cursor }),
    };
    return withServiceBusyRetry(
      () => this.dependencies.api.getMyVotes(request),
      this.dependencies.wait,
    );
  }

  /** 状態を更新した後、全購読者へ同じスナップショットを通知する。 */
  private publish(state: MyVotesFlowState): void {
    this.state = state;
    for (const subscriber of this.subscribers) {
      subscriber(state);
    }
  }
}
